import React from "react";
import { Tooltip } from "@mui/material";
import BaseCheckbox from "@reusables/BaseCheckbox";
import ReservedQuantityIndicator from "@ready/ReservedQuantityIndicator";
import { ReactComponent as UnitEquivalentsSVG } from "@assets/icons/unitEquivalents.svg";
import {
    isUnitEquivalentSelected,
    roundTo,
    toggleUnitEquivalentSelection,
    UnitEquivalentItem,
} from "@helpers/utils";

type UnitEquivalentsSelectorProps = {
    rowId: number;
    baseQuantity?: number | null;
    baseUnitName?: string | null;
    unitEquivalents?: UnitEquivalentItem[];
    selectedUnitEquivalents: Record<number, UnitEquivalentItem[]>;
    setSelectedUnitEquivalents: React.Dispatch<
        React.SetStateAction<Record<number, UnitEquivalentItem[]>>
    >;
    reservedQuantity?: number | null;
    showBaseQuantity?: boolean;
    placeholder?: string;
};

export default function UnitEquivalentsSelector({
    rowId,
    baseQuantity,
    baseUnitName,
    unitEquivalents,
    selectedUnitEquivalents,
    setSelectedUnitEquivalents,
    reservedQuantity,
    showBaseQuantity = true,
    placeholder = "0",
}: UnitEquivalentsSelectorProps) {
    const hasBaseQuantity = typeof baseQuantity === "number" && Number.isFinite(baseQuantity);

    const formatEquivalentValue = (quantity: number) => {
        if (!hasBaseQuantity || !quantity) {
            return placeholder;
        }

        const value = (baseQuantity as number) / quantity;
        if (!Number.isFinite(value)) {
            return placeholder;
        }

        return String(roundTo(value, 2));
    };

    const selectedItems = selectedUnitEquivalents[rowId] ?? [];
    const showChips = hasBaseQuantity && selectedItems.length > 0;

    return (
        <div className="text-center relative flex gap-2">
            <div className="flex items-center gap-2">
                {showBaseQuantity && (
                    <span>
                        {hasBaseQuantity
                            ? `${baseQuantity}${baseUnitName ? ` ${baseUnitName}` : ""}`
                            : placeholder}
                    </span>
                )}

                <ReservedQuantityIndicator reservedQuantity={reservedQuantity} />

                {showChips &&
                    selectedItems.map((item, idx) => (
                        <span
                            key={idx}
                            className="text-xs text-accent bg-[#F4F4F4] px-1.5 py-0.5 rounded"
                        >
                            {formatEquivalentValue(item.quantity)}{" "}
                            {item.unit.code || item.unit.name}
                        </span>
                    ))}
            </div>

            {unitEquivalents && unitEquivalents.length > 0 && (
                <Tooltip
                    placement="right"
                    arrow
                    disableFocusListener
                    disableTouchListener
                    componentsProps={{
                        tooltip: {
                            sx: {
                                backgroundColor: "#ffffffff",
                                color: "#3C3769",
                                padding: "12px",
                                borderRadius: "6px",
                                boxShadow: "0px 15px 50px 0px rgba(69, 88, 157, 0.23)",
                                "& .MuiTooltip-arrow": {
                                    color: "#ffffff",
                                },
                            },
                        },
                    }}
                    title={
                        <div
                            className="flex flex-col gap-2"
                            onClick={(e) => e.stopPropagation()}
                            onMouseDown={(e) => e.stopPropagation()}
                        >
                            {unitEquivalents.map((item, i) => (
                                <BaseCheckbox
                                    key={i}
                                    label={`${formatEquivalentValue(item.quantity)} ${
                                        item.unit.name
                                    }`}
                                    value={isUnitEquivalentSelected(
                                        rowId,
                                        item,
                                        selectedUnitEquivalents
                                    )}
                                    onChange={() =>
                                        toggleUnitEquivalentSelection(
                                            rowId,
                                            item,
                                            selectedUnitEquivalents,
                                            setSelectedUnitEquivalents
                                        )
                                    }
                                />
                            ))}
                        </div>
                    }
                >
                    <div className={"text-[#B1B2E1]"}>
                        <UnitEquivalentsSVG />
                    </div>
                </Tooltip>
            )}
        </div>
    );
}
